import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Countdown from "./Countdown";
import { scrollToId } from "./Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINES = [
  { text: "LAUNCH", cls: "text-white" },
  { text: "WHERE YOU", cls: "text-outline-signal" },
  { text: "LAND.", cls: "text-white" },
];

export default function Hero({ active = true }) {
  const ref = useRef(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const imgX = useTransform(sx, (v) => v * -34);
  const imgY = useTransform(sy, (v) => v * -22);

  const MaskedLine = ({ children, delay, className }) => (
    <span className="block overflow-hidden pb-1 -mb-1">
      <motion.span
        className={`block ${className || ""}`}
        initial={{ y: "115%" }}
        animate={active ? { y: 0 } : { y: "115%" }}
        transition={{ delay, duration: 1, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const quickJoin = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email, source: "hero" });
      toast.success(data.already_joined ? "You're already on the signal." : "You're on the signal. Welcome aboard.");
      setEmail("");
      window.dispatchEvent(new Event("lp:waitlist-joined"));
    } catch {
      toast.error("Signal interrupted — check that email and retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative min-h-[100svh] flex flex-col overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY, x: imgX }} className="absolute inset-[-6%] z-0">
        <motion.img
          src="https://images.pexels.com/photos/2167382/pexels-photo-2167382.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Concert stage bathed in broadcast light — Launchpad TV creator-first connected TV network"
          width={1880}
          height={1300}
          fetchPriority="high"
          decoding="async"
          style={{ y: imgY }}
          className="w-full h-full object-cover opacity-40 scale-110"
        />
        <div className="absolute inset-0 bg-indigo opacity-35 mix-blend-color" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/55 to-obsidian" />
        <div className="absolute inset-0 scanlines" />
        <div className="absolute inset-0 noise-overlay" />
      </motion.div>

      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-2vw] left-0 right-0 z-[1] overflow-hidden"
      >
        <div className="font-display font-800 font-extrabold text-outline text-[19vw] leading-[0.85] whitespace-nowrap text-center select-none">
          LAUNCHPAD
        </div>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 flex-1 flex flex-col items-center justify-center text-center max-w-6xl mx-auto px-4 sm:px-8 pt-28 sm:pt-32 pb-28 sm:pb-48 w-full"
      >
        <MaskedLine delay={0.35}>
          <span className="inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] uppercase text-slate-400 border border-white/10 glass-panel px-4 py-2">
            <span className="h-1.5 w-1.5 rounded-full bg-signal animate-live-dot" />
            Talent is universal. Visibility is not.
          </span>
        </MaskedLine>

        <h1 className="mt-8 font-head font-black tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-headline">
          {LINES.map((l, i) => (
            <MaskedLine key={l.text} delay={0.5 + i * 0.14} className={l.cls}>
              {l.text}
            </MaskedLine>
          ))}
        </h1>

        <MaskedLine delay={0.95}>
          <p className="mt-6 max-w-2xl mx-auto text-sm sm:text-base text-slate-400 leading-relaxed" data-testid="hero-subtext">
            From neighborhoods to nations, Launchpad connects creators and communities
            with global audiences through streaming, discovery, and shared culture.
            Start where you are. Be seen everywhere.
          </p>
        </MaskedLine>

        <MaskedLine delay={1.1}>
          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 w-full sm:w-auto">
            <button
              data-testid="hero-claim-access-button"
              onClick={() => scrollToId("#contact")}
              className="group flex items-center justify-center gap-3 bg-signal text-obsidian font-bold font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 signal-glow hover:bg-white transition-colors duration-300"
            >
              Join the Movement
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-watch-trailer-button"
              onClick={() => scrollToId("#trailer")}
              className="flex items-center justify-center gap-3 glass-panel text-white font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-signal/60 transition-colors duration-300"
            >
              <Play size={14} className="text-signal" />
              Watch the Teaser
            </button>
          </div>
        </MaskedLine>

        <motion.form
          onSubmit={quickJoin}
          initial={{ opacity: 0, y: 24 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ delay: 1.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 w-full max-w-md flex"
          data-testid="hero-waitlist-form"
        >
          <input
            data-testid="hero-waitlist-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourchannel.tv"
            className="flex-1 bg-white/5 border border-white/15 border-r-0 px-4 py-3.5 font-mono text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-signal/70 transition-colors"
          />
          <button
            data-testid="hero-waitlist-submit-button"
            type="submit"
            disabled={loading}
            className="bg-signal text-obsidian font-bold font-mono text-[11px] tracking-[0.2em] uppercase px-6 hover:bg-white hover:text-obsidian transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? "..." : "Join"}
          </button>
        </motion.form>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ delay: 1.45, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 w-full max-w-3xl"
        >
          <Countdown />
        </motion.div>
      </motion.div>
    </section>
  );
}
